import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { toast, ToastContainer } from 'react-toastify'
import { showErrorToast } from '../../../shared/utils/apiErrorMessage'
import { KTSVG } from '../../../../_metronic/helpers'
import { useAuth } from '../../auth'
import { Company, CompanyAdmin, Crew, Rank, Vessel } from '../core/_models'
import {
  ComplaintStatus,
  ComplaintWorkflowStage,
  CreateCrewComplaintRequest,
  CrewComplaint,
  UpdateCrewComplaintRequest,
  createCrewComplaint,
  crewComplaintAttachmentDownloadUrl,
  crewComplaintAttachmentViewUrl,
  deleteCrewComplaint,
  deleteCrewComplaintAttachment,
  getCompanyAdminList,
  getCompanyList,
  getCrewComplaintById,
  getCrewComplaints,
  getCrewList,
  getRanksforList,
  submitCrewComplaint,
  updateCrewComplaint,
  uploadCrewComplaintAttachment,
} from '../core/_requests'
import CrewComplaintModal, { ComplaintFormValues } from '../components/CrewComplaintModal'
import ViewCrewComplaintModal from '../components/ViewCrewComplaintModal'
import CrewComplaintReviewModal from '../components/CrewComplaintReviewModal'
import CrewComplaintAttachmentsModal from '../components/CrewComplaintAttachmentsModal'
import { FileViewerModal } from '../../QHSE/components/FileViewerModal'
import {
  buildCrewSignOnDateMap,
  getCrewSignOnDate,
  getCrewSignOnMonth,
  isDateBeforeSignOn,
} from '../core/signOnDateUtils'
import { fetchAccessibleVesselsForCurrentUser, normalizeAccessibleVessels } from '../../../shared/access/accessibleVessels'
import { TableSkeleton } from '../../../shared/components/LoadingSkeletons'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'
import {
  CREWING_ACTION_PERMISSIONS,
  CREWING_READ_PERMISSIONS,
  useCrewingCanRead,
  useCrewingPermissionRevision,
} from '../core/crewingPermissionAccess'
import { useCrewingRoleAccess } from '../core/crewingRoleAccess'
import {
  applyCrewingPresentationFilters,
  allowCompanyPresentationFilters,
  extractRows,
} from '../core/crewingPresentationFilters'
import { filterVesselsForPresentationScope } from '../../../shared/access/tenantScopeMatch'

type SortKey = 'seafarerName' | 'reportDate' | 'vesselName'
type SortConfig = { key: SortKey; direction: 'asc' | 'desc' }

const toDateInput = (value?: string | null): string => {
  if (!value) return ''
  return value.length >= 10 ? value.substring(0, 10) : value
}

const normalizeComplaintType = (value?: string | null): string => {
  const raw = String(value || '').trim()
  if (!raw) return ''
  const key = raw.toLowerCase().replace(/[_\s-]+/g, '')
  if (key === 'bullying') return 'BULLYING'
  if (key === 'physicalharassment') return 'PHYSICAL_HARASSMENT'
  if (key === 'mentalharassment') return 'MENTAL_HARASSMENT'
  if (key === 'sexualharassment') return 'SEXUAL_HARASSMENT'
  return raw
}

const toYesNo = (value?: boolean | null): '' | 'YES' | 'NO' => {
  if (value === true) return 'YES'
  if (value === false) return 'NO'
  return ''
}

const fromYesNo = (value?: string): boolean | undefined => {
  if (value === 'YES') return true
  if (value === 'NO') return false
  return undefined
}

const toOnboardReviewRole = (value?: string | null): '' | 'HOD' | 'MASTER' => {
  const normalized = String(value || '').trim().toUpperCase()
  if (normalized === 'HOD' || normalized === 'MASTER') {
    return normalized
  }
  return ''
}

const toAssignedHodRole = (value?: string | null): '' | 'CHIEF_OFFICER' | 'CHIEF_ENGINEER' => {
  const normalized = String(value || '').trim().toUpperCase()
  if (normalized === 'CHIEF_OFFICER' || normalized === 'CHIEF_ENGINEER') {
    return normalized
  }
  return ''
}

// Complaint HOD/master branching is an onboard workflow rule, not sidebar or RBAC gating.
const resolveCurrentUserHodRole = (rankNameRaw?: string | null): 'CHIEF_OFFICER' | 'CHIEF_ENGINEER' | undefined => {
  const rankName = String(rankNameRaw || '').toLowerCase()
  if (rankName.includes('chief officer') || rankName.includes('c/o')) {
    return 'CHIEF_OFFICER'
  }
  if (rankName.includes('chief engineer') || rankName.includes('c/e')) {
    return 'CHIEF_ENGINEER'
  }
  return undefined
}

const isMasterRankLabel = (rankName?: string | null) => {
  const normalized = String(rankName || '').toLowerCase()
  return normalized.includes('master')
}

const isHodRankLabel = (rankName?: string | null) => {
  const normalized = String(rankName || '').toLowerCase()
  return (
    normalized.includes('chief officer') ||
    normalized.includes('chief engineer') ||
    normalized.includes('c/o') ||
    normalized.includes('c/e')
  )
}

const getWorkflowStage = (item?: CrewComplaint | null): ComplaintWorkflowStage | undefined => {
  if (!item) return undefined
  if (item.workflowStage) {
    if (
      item.workflowStage === 'MASTER_REVIEW' &&
      !item.onboardReviewRole &&
      !isHodRankLabel(item.rankName)
    ) {
      return 'HOD_REVIEW'
    }
    return item.workflowStage
  }
  if (item.status === 'SUBMITTED') {
    if (isMasterRankLabel(item.rankName)) return 'OFFICE_REVIEW'
    if (isHodRankLabel(item.rankName)) return 'MASTER_REVIEW'
    return 'HOD_REVIEW'
  }
  if (item.status === 'REVIEWED' || item.status === 'REJECTED') return 'CLOSED'
  return undefined
}

const todayIso = () => new Date().toISOString().slice(0, 10)

const resolveUserVesselId = (user: any): number | undefined => {
  const id = Number(user?.vessel?.id ?? user?.userDetails?.vessel?.id ?? user?.vesselId ?? user?.userDetails?.vesselId ?? 0)
  return id > 0 ? id : undefined
}

const buildEmptyForm = (): ComplaintFormValues => ({
  recordDate: todayIso(),
  reportDate: todayIso(),
  seafarerName: '',
  complaintType: '',
  description: '',
  assignedHodRole: '',
  hodMasterResponse: '',
  hodMasterResponseDate: '',
  onboardPreventRecurrence: '',
  hodSignature: '',
  masterSignature: '',
  onboardReviewRole: '',
  onboardResolved: '',
  officeResolved: '',
  officeActionTaken: '',
  officeActionDate: '',
  officePreventRecurrence: '',
  officeWelfareOfficerSignature: '',
  officeResolutionDate: '',
  authoritiesActionTaken: '',
  authoritiesActionDate: '',
  authoritiesPreventRecurrence: '',
  authoritiesWelfareOfficerSignature: '',
  authoritiesResolutionDate: '',
  officeRemarks: '',
})

const mapComplaintToForm = (item: CrewComplaint): ComplaintFormValues => ({
  id: item.id,
  crewId: item.crewId,
  vesselId: item.vesselId ?? undefined,
  rankId: item.rankId ?? undefined,
  recordDate: toDateInput(item.recordDate),
  reportDate: toDateInput(item.reportDate),
  seafarerName: item.seafarerName || '',
  complaintType: normalizeComplaintType(item.complaintType),
  description: item.description || '',
  assignedHodRole: toAssignedHodRole(item.assignedHodRole),
  hodMasterResponse: item.hodMasterResponse || '',
  hodMasterResponseDate: toDateInput(item.hodMasterResponseDate),
  onboardPreventRecurrence: item.onboardPreventRecurrence || '',
  hodSignature: item.hodSignature || '',
  masterSignature: item.masterSignature || '',
  onboardReviewRole: toOnboardReviewRole(item.onboardReviewRole),
  onboardResolved: toYesNo(item.onboardResolved),
  officeResolved: toYesNo(item.officeResolved),
  officeActionTaken: item.officeActionTaken || '',
  officeActionDate: toDateInput(item.officeActionDate),
  officePreventRecurrence: item.officePreventRecurrence || '',
  officeWelfareOfficerSignature: item.officeWelfareOfficerSignature || '',
  officeResolutionDate: toDateInput(item.officeResolutionDate),
  authoritiesActionTaken: item.authoritiesActionTaken || '',
  authoritiesActionDate: toDateInput(item.authoritiesActionDate),
  authoritiesPreventRecurrence: item.authoritiesPreventRecurrence || '',
  authoritiesWelfareOfficerSignature: item.authoritiesWelfareOfficerSignature || '',
  authoritiesResolutionDate: toDateInput(item.authoritiesResolutionDate),
  officeRemarks: item.officeRemarks || '',
})

const CrewComplaints: FC = () => {
  const { currentUser, hasPermission, isSuperAdmin } = useAuth()
  const {
    isCrew,
    isOfficeRole,
    isCompanyGroupAdmin,
    isCompanyAdmin: baseIsCompanyAdmin,
    isOperator,
  } = useCrewingRoleAccess()
  const roleId = currentUser?.role?.id

  const operatorCompanyAdminId = Number((currentUser as any)?.companyAdminId ?? (currentUser as any)?.companyAdmin?.id ?? 0) || undefined
  const isCompanyAdmin = baseIsCompanyAdmin || (isOperator && !!operatorCompanyAdminId)
  const crewRankId = Number((currentUser as any)?.rank?.id ?? 0)
  const crewRankName = String((currentUser as any)?.rank?.rank ?? '').toLowerCase()
  const isCrewMaster = isCrew && (crewRankId === 1 || crewRankName.includes('master'))
  const isCrewHod =
    isCrew &&
    (crewRankName.includes('chief officer') ||
      crewRankName.includes('chief engineer') ||
      crewRankName.includes('c/o') ||
      crewRankName.includes('c/e'))
  const currentUserHodRole = resolveCurrentUserHodRole(crewRankName)
  const roleEntityId = Number((currentUser as any)?.roleEntityId ?? 0)
  const currentCrewRoleEntityId = isCrew ? roleEntityId : 0
  const loginUsername = String(
    (currentUser as any)?.username ||
    (currentUser as any)?.userDetails?.username ||
    currentUser?.roleEntityName ||
    ''
  ).trim()

  const canCreate = isSuperAdmin || hasPermission('CREATE_CREW_COMPLAINT')
  const canReview = isSuperAdmin || (hasPermission('APPROVE_CREW_COMPLAINTS') && isOfficeRole)
  const useCrewDropdownFilter = isCompanyGroupAdmin || isCompanyAdmin

  const showCompanyFilter = isSuperAdmin
  const showSubCompanyFilter = isSuperAdmin || isCompanyGroupAdmin
  const showVesselFilter = isSuperAdmin || isCompanyGroupAdmin || isCompanyAdmin || isOperator

  const fixedCompanyGroupId = useMemo(() => {
    if (isCompanyGroupAdmin) {
      return Number((currentUser as any)?.companyGroupAdminId ?? (currentUser as any)?.companyGroupAdmin?.id ?? (roleEntityId || 0)) || undefined
    }
    if (isCompanyAdmin) {
      return Number((currentUser as any)?.companyGroupAdminId ?? (currentUser as any)?.companyAdmin?.cga?.id ?? 0) || undefined
    }
    return undefined
  }, [currentUser, isCompanyGroupAdmin, isCompanyAdmin, roleEntityId])

  const fixedCompanyAdminId = useMemo(() => {
    if (!isCompanyAdmin) return undefined
    return Number((currentUser as any)?.companyAdminId ?? (currentUser as any)?.companyAdmin?.id ?? (roleEntityId || 0)) || undefined
  }, [currentUser, isCompanyAdmin, roleEntityId])

  const allowCompanyFilters = allowCompanyPresentationFilters(isSuperAdmin, roleId, isOperator)

  const canReadComplaints = useCrewingCanRead(CREWING_READ_PERMISSIONS.COMPLAINT)
  const permissionRevision = useCrewingPermissionRevision()

  const [items, setItems] = useState<CrewComplaint[]>([])
  const [ranks, setRanks] = useState<Rank[]>([])
  const [crewMembers, setCrewMembers] = useState<Crew[]>([])
  const [companyGroups, setCompanyGroups] = useState<CompanyAdmin[]>([])
  const [subCompanies, setSubCompanies] = useState<Company[]>([])
  const [vessels, setVessels] = useState<Vessel[]>([])

  const [selectedCompanyGroupId, setSelectedCompanyGroupId] = useState<number | ''>('')
  const [selectedCompanyAdminId, setSelectedCompanyAdminId] = useState<number | ''>('')
  const [selectedVesselId, setSelectedVesselId] = useState<number | ''>('')

  const { state: globalState, vessels: globalVessels } = useGlobalFilter()

  useEffect(() => {
    setSearchTerm(globalState.searchQuery || '')
  }, [globalState.searchQuery])

  useEffect(() => {
    if (globalState.vessel?.id) {
      setSelectedVesselId(Number(globalState.vessel.id))
    } else if (isCrew && currentUser?.vessel?.id) {
      setSelectedVesselId(Number(currentUser.vessel.id))
    } else {
      setSelectedVesselId('')
    }

    if (globalState.company) {
      if (globalState.company.type === 'company_group') {
        setSelectedCompanyGroupId(Number(globalState.company.id))
        setSelectedCompanyAdminId('')
      } else {
        setSelectedCompanyAdminId(Number(globalState.company.id))
        setSelectedCompanyGroupId('')
      }
    } else {
      setSelectedCompanyGroupId(isCompanyGroupAdmin ? (fixedCompanyGroupId ?? '') : '')
      setSelectedCompanyAdminId(isCompanyAdmin ? (fixedCompanyAdminId ?? '') : '')
    }
  }, [
    globalState.vessel,
    globalState.company,
    isCrew,
    currentUser,
    isCompanyGroupAdmin,
    isCompanyAdmin,
    fixedCompanyGroupId,
    fixedCompanyAdminId,
  ])

  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCrewId, setSelectedCrewId] = useState<number | ''>('')
  const [statusFilter, setStatusFilter] = useState<'All' | ComplaintStatus>('All')
  const [monthFilter, setMonthFilter] = useState('')
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'seafarerName', direction: 'asc' })
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const [form, setForm] = useState<ComplaintFormValues>(buildEmptyForm())
  const [pendingFiles, setPendingFiles] = useState<File[]>([])

  const [editingComplaint, setEditingComplaint] = useState<CrewComplaint | null>(null)
  const [viewComplaint, setViewComplaint] = useState<CrewComplaint | null>(null)
  const [reviewComplaint, setReviewComplaint] = useState<CrewComplaint | null>(null)
  const [attachmentsComplaint, setAttachmentsComplaint] = useState<CrewComplaint | null>(null)

  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isViewModalOpen, setIsViewModalOpen] = useState(false)
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false)
  const [isAttachmentsModalOpen, setIsAttachmentsModalOpen] = useState(false)
  const [fileModal, setFileModal] = useState<{ visible: boolean; title: string; fileName?: string; viewUrl?: string; downloadUrl?: string }>({ visible: false, title: '' })

  const [editMode, setEditMode] = useState<'create' | 'edit'>('create')

  const [modalCrewMembers, setModalCrewMembers] = useState<Crew[]>([])
  const modalCrewFetchSeqRef = useRef(0)
  const complaintRosterPeriod = (form.reportDate || form.recordDate || new Date().toISOString()).slice(0, 7)
  const modalSignOnDateByCrewId = useMemo(() => buildCrewSignOnDateMap(modalCrewMembers), [modalCrewMembers])

  useEffect(() => {
    if (!isEditModalOpen || !canReadComplaints) return
    const requestSeq = ++modalCrewFetchSeqRef.current
    setModalCrewMembers([])
    getCrewList({period: complaintRosterPeriod}).then((crew) => {
      if (requestSeq === modalCrewFetchSeqRef.current) setModalCrewMembers(crew)
    }).catch((error) => {
      if (requestSeq === modalCrewFetchSeqRef.current) showErrorToast(error, 'Failed to fetch complaint crew')
    })
    return () => { modalCrewFetchSeqRef.current += 1 }
  }, [isEditModalOpen, canReadComplaints, complaintRosterPeriod])

  const didInitFiltersRef = useRef(false)

  useEffect(() => {
    if (!roleId || !canReadComplaints) return
    fetchComplaints()
    getRanksforList().then(setRanks).catch(() => undefined)
    getCrewList().then(setCrewMembers).catch(() => undefined)

    if (showCompanyFilter) {
      getCompanyAdminList().then(setCompanyGroups).catch(() => undefined)
    }
    if (showSubCompanyFilter) {
      getCompanyList().then(setSubCompanies).catch(() => undefined)
    }
    if (showVesselFilter) {
      fetchAccessibleVesselsForCurrentUser().then((list) => setVessels(normalizeAccessibleVessels(list))).catch(() => undefined)
    }
  }, [roleId, canReadComplaints, showCompanyFilter, showSubCompanyFilter, showVesselFilter, permissionRevision])

  useEffect(() => {
    if (didInitFiltersRef.current) return

    if (isCompanyGroupAdmin) {
      const cgaId = (currentUser as any)?.companyGroupAdminId ?? (currentUser as any)?.companyGroupAdmin?.id
      if (cgaId) setSelectedCompanyGroupId(cgaId)
    }

    if (isCompanyAdmin) {
      const caId = (currentUser as any)?.companyAdminId ?? (currentUser as any)?.companyAdmin?.id
      if (caId) setSelectedCompanyAdminId(caId)
    }

    if (isCrew) {
      const vesselId = resolveUserVesselId(currentUser as any)
      if (vesselId) setSelectedVesselId(vesselId)
    }

    didInitFiltersRef.current = true
  }, [currentUser, isCompanyGroupAdmin, isCompanyAdmin, isCrew])

const fetchComplaints = async () => {
  setIsLoading(true)
  try {
    const data = extractRows<CrewComplaint>(await getCrewComplaints())
    setItems(canReadComplaints ? data : [])
  } catch (error: any) {
    showErrorToast(error, 'Failed to load complaints')
  } finally {
    setIsLoading(false)
  }
}

const effectiveVessels = useMemo(() => {
  const map = new Map<number, Vessel>()
  ;(globalVessels || []).forEach((v) => {
    if (v?.id) map.set(Number(v.id), v)
  })
  ;(vessels || []).forEach((v) => {
    if (v?.id) map.set(Number(v.id), v)
  })
  return Array.from(map.values())
}, [globalVessels, vessels])

const vesselById = useMemo(
  () => new Map(effectiveVessels.map((v) => [v.id, v])),
  [effectiveVessels]
)

const crewFilterOptions = useMemo(() => {
  const visibleCrew = canReadComplaints ? crewMembers : []
  const uniqueById = new Map<number, string>()

  applyCrewingPresentationFilters(visibleCrew, {
    selectedVesselId,
    selectedCrewId,
    allowCompanyPresentationFilters: allowCompanyFilters,
    selectedCompanyGroupId: allowCompanyFilters ? selectedCompanyGroupId : '',
    selectedCompanyAdminId: allowCompanyFilters ? selectedCompanyAdminId : '',
  }, {
    vesselById,
  }).forEach((crew) => {
    const crewId = Number(crew.id)
    if (Number.isFinite(crewId) && crewId > 0) {
      uniqueById.set(crewId, String(crew.name || `Crew ${crewId}`))
    }
  })

  if (uniqueById.size === 0 && canReadComplaints) {
    applyCrewingPresentationFilters(items, {
      selectedVesselId,
      allowCompanyPresentationFilters: allowCompanyFilters,
      selectedCompanyGroupId: allowCompanyFilters ? selectedCompanyGroupId : '',
      selectedCompanyAdminId: allowCompanyFilters ? selectedCompanyAdminId : '',
    }, {
      vesselById,
    }).forEach((row) => {
      const typed = row
      const isDraftVisibleToUser =
        typed.status !== 'DRAFT' ||
        isSuperAdmin ||
        (isCrew && currentCrewRoleEntityId > 0 && typed.crewId === currentCrewRoleEntityId)
      if (!isDraftVisibleToUser) return
      if (!uniqueById.has(typed.crewId)) {
        uniqueById.set(typed.crewId, typed.crewName || typed.seafarerName || `Crew ${typed.crewId}`)
      }
    })
  }

  return Array.from(uniqueById.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name))
}, [
  crewMembers,
  items,
  selectedCompanyGroupId,
  selectedCompanyAdminId,
  selectedVesselId,
  selectedCrewId,
  allowCompanyFilters,
  canReadComplaints,
  isSuperAdmin,
  isCrew,
  currentCrewRoleEntityId,
  vesselById,
])

useEffect(() => {
  if (!selectedCrewId) return
  const exists = crewFilterOptions.some((crew) => crew.id === Number(selectedCrewId))
  if (!exists) setSelectedCrewId('')
}, [selectedCrewId, crewFilterOptions])

const crewSignOnDateByCrewId = useMemo(
  () => buildCrewSignOnDateMap(crewMembers),
  [crewMembers]
)

// The API validates historical months against all service periods.
const handleMonthFilterChange = (value: string) => {
  setMonthFilter(value)
}

const filteredItems = useMemo(() => {
  if (!canReadComplaints) return []

  return applyCrewingPresentationFilters(items, {
    searchTerm: useCrewDropdownFilter ? undefined : searchTerm,
    selectedCrewId: useCrewDropdownFilter ? selectedCrewId : undefined,
    selectedVesselId,
    allowCompanyPresentationFilters: allowCompanyFilters,
    selectedCompanyGroupId: allowCompanyFilters ? selectedCompanyGroupId : '',
    selectedCompanyAdminId: allowCompanyFilters ? selectedCompanyAdminId : '',
  }, {
    vesselById,
    extraPredicate: (row) => {
      const typed = row
      const isDraftVisibleToUser =
        typed.status !== 'DRAFT' ||
        isSuperAdmin ||
        (isCrew && currentCrewRoleEntityId > 0 && typed.crewId === currentCrewRoleEntityId)
      const term = searchTerm.trim().toLowerCase()
      const matchesCrew = !selectedCrewId || typed.crewId === Number(selectedCrewId)
      const matchesSearch =
        !term ||
        (typed.crewName || '').toLowerCase().includes(term) ||
        (typed.seafarerName || '').toLowerCase().includes(term) ||
        (typed.vesselName || '').toLowerCase().includes(term) ||
        (typed.complaintType || '').toLowerCase().includes(term) ||
        (typed.description || '').toLowerCase().includes(term)
      const matchesStatus = statusFilter === 'All' || typed.status === statusFilter
      const reportMonth = toDateInput(typed.reportDate).slice(0, 7)
      const matchesMonth = !monthFilter || reportMonth === monthFilter
      const stage = getWorkflowStage(typed)
      const hiddenForOtherHod =
        isCrewHod &&
        stage === 'HOD_REVIEW' &&
        !!typed.assignedHodRole &&
        typed.assignedHodRole !== currentUserHodRole
      return (
        isDraftVisibleToUser &&
        !hiddenForOtherHod &&
        matchesCrew &&
        matchesSearch &&
        matchesStatus &&
        matchesMonth
      )
    },
  })
}, [
  items,
  canReadComplaints,
  searchTerm,
  selectedCrewId,
  statusFilter,
  monthFilter,
  selectedCompanyGroupId,
  selectedCompanyAdminId,
  selectedVesselId,
  vesselById,
  useCrewDropdownFilter,
  allowCompanyFilters,
  crewSignOnDateByCrewId,
  isSuperAdmin,
  isCrew,
  currentCrewRoleEntityId,
  isCrewHod,
  currentUserHodRole,
])

const reminderItems = useMemo(() => {
  return items.filter((item) => Boolean(item.authoritiesReminderAt))
}, [items])

const setField = (field: keyof ComplaintFormValues, value: string | number | undefined) => {
  setForm((prev) => ({ ...prev, [field]: value }))
}

useEffect(() => {
  setCurrentPage(1)
}, [searchTerm, selectedCrewId, statusFilter, monthFilter, selectedCompanyGroupId, selectedCompanyAdminId, selectedVesselId])

const handleSort = (key: SortKey) => {
  setSortConfig((prev) => ({
    key,
    direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
  }))
}

const handleTableHorizontalWheelScroll = (event: React.WheelEvent<HTMLDivElement>) => {
  const container = event.currentTarget
  if (container.scrollWidth <= container.clientWidth) return

  // Keep vertical scrolling native; use Shift + wheel for horizontal intent.
  const isShiftHorizontalIntent = event.shiftKey && Math.abs(event.deltaY) > Math.abs(event.deltaX)
  if (!isShiftHorizontalIntent || event.deltaY === 0) return

  const isScrollingRight = event.deltaY > 0
  const canScrollContainerRight = container.scrollLeft + container.clientWidth < container.scrollWidth - 1
  const canScrollContainerLeft = container.scrollLeft > 0
  if ((isScrollingRight && !canScrollContainerRight) || (!isScrollingRight && !canScrollContainerLeft)) return

  event.preventDefault()
  container.scrollLeft += event.deltaY
}

const sortedItems = useMemo(() => {
  const rows = [...filteredItems]
  rows.sort((a, b) => {
    const first = a[sortConfig.key] ?? ''
    const second = b[sortConfig.key] ?? ''

    if (sortConfig.key === 'reportDate') {
      const firstDate = toDateInput(String(first || ''))
      const secondDate = toDateInput(String(second || ''))
      return sortConfig.direction === 'asc'
        ? firstDate.localeCompare(secondDate)
        : secondDate.localeCompare(firstDate)
    }

    return sortConfig.direction === 'asc'
      ? String(first).localeCompare(String(second))
      : String(second).localeCompare(String(first))
  })
  return rows
}, [filteredItems, sortConfig])

const indexOfLast = currentPage * rowsPerPage
const indexOfFirst = indexOfLast - rowsPerPage
const currentRows = sortedItems.slice(indexOfFirst, indexOfLast)
const totalPages = Math.ceil(sortedItems.length / rowsPerPage)

useEffect(() => {
  if (!form.crewId || editMode !== 'create') return
  const selectedCrew = modalCrewMembers.find((c) => c.id === Number(form.crewId))
  if (!selectedCrew) return

  setForm((prev) => ({
    ...prev,
    seafarerName: prev.seafarerName || selectedCrew.name,
    vesselId: prev.vesselId || selectedCrew.vesselId || selectedCrew.vessel?.id,
    rankId: prev.rankId || selectedCrew.rankId,
  }))
}, [form.crewId, modalCrewMembers, editMode])

const openCreate = () => {
  const fresh = buildEmptyForm()
  if (isCrew) {
    fresh.seafarerName = (currentUser as any)?.roleEntityName || currentUser?.username || ''
    const meCrewId = Number((currentUser as any)?.roleEntityId ?? 0)
    if (meCrewId > 0) {
      fresh.crewId = meCrewId
      const signOnDate = getCrewSignOnDate(meCrewId, crewSignOnDateByCrewId)
      if (signOnDate) {
        fresh.recordDate = signOnDate
        fresh.reportDate = signOnDate
      }
    }
    const myVesselId = resolveUserVesselId(currentUser as any)
    if (myVesselId) {
      fresh.vesselId = myVesselId
    }
  }
  setEditMode('create')
  setEditingComplaint(null)
  setForm(fresh)
  setPendingFiles([])
  setIsEditModalOpen(true)
}

const openEdit = (item: CrewComplaint) => {
  setEditMode('edit')
  setPendingFiles([])
  setIsSaving(true)
  getCrewComplaintById(item.id)
    .then((fullComplaint) => {
      setEditingComplaint(fullComplaint)
      setForm(mapComplaintToForm(fullComplaint))
      setIsEditModalOpen(true)
    })
    .catch((error: any) => {
      showErrorToast(error, 'Failed to load complaint details')
    })
    .finally(() => setIsSaving(false))
}

const openView = (item: CrewComplaint) => {
  setIsSaving(true)
  getCrewComplaintById(item.id)
    .then((fullComplaint) => {
      setViewComplaint(fullComplaint)
      setIsViewModalOpen(true)
    })
    .catch((error: any) => {
      showErrorToast(error, 'Failed to load complaint details')
    })
    .finally(() => setIsSaving(false))
}

const openReview = (item: CrewComplaint) => {
  setIsSaving(true)
  getCrewComplaintById(item.id)
    .then((fullComplaint) => {
      setReviewComplaint(fullComplaint)
      const mapped = mapComplaintToForm(fullComplaint)
      const stage = getWorkflowStage(fullComplaint)
      if (stage === 'HOD_REVIEW') {
        mapped.hodMasterResponse = fullComplaint.hodResponse || fullComplaint.hodMasterResponse || ''
        mapped.hodMasterResponseDate = toDateInput(fullComplaint.hodResponseDate || fullComplaint.hodMasterResponseDate)
      }
      if (stage === 'MASTER_REVIEW') {
        mapped.hodMasterResponse = fullComplaint.masterResponse || fullComplaint.hodMasterResponse || ''
        mapped.hodMasterResponseDate = toDateInput(fullComplaint.masterResponseDate || fullComplaint.hodMasterResponseDate)
      }
      if (loginUsername) {
        if (stage === 'HOD_REVIEW' && !mapped.hodSignature) {
          mapped.hodSignature = loginUsername
        }
        if (stage === 'MASTER_REVIEW' && !mapped.masterSignature) {
          mapped.masterSignature = loginUsername
        }
        if (stage === 'OFFICE_REVIEW' && !mapped.officeWelfareOfficerSignature) {
          mapped.officeWelfareOfficerSignature = loginUsername
        }
        if (stage === 'AUTHORITIES_REVIEW' && !mapped.authoritiesWelfareOfficerSignature) {
          mapped.authoritiesWelfareOfficerSignature = loginUsername
        }
      }
      setForm(mapped)
      setIsReviewModalOpen(true)
    })
    .catch((error: any) => {
      showErrorToast(error, 'Failed to load complaint details')
    })
    .finally(() => setIsSaving(false))
}

const saveComplaint = async () => {
  if (!form.description.trim()) {
    toast.error('Complaint description is required')
    return
  }
  if (!form.complaintType) {
    toast.error('Complaint type is required')
    return
  }
  const selectedCrew = modalCrewMembers.find((c) => c.id === (editingComplaint?.crewId || form.crewId))
  const selectedRankName =
    (selectedCrew as any)?.rankName ||
    (selectedCrew?.rankId ? ranks.find((r) => r.id === selectedCrew.rankId)?.rank : undefined) ||
    editingComplaint?.rankName ||
    (form.rankId ? ranks.find((r) => r.id === form.rankId)?.rank : undefined)
  const isMasterSeafarer = isMasterRankLabel(selectedRankName)
  const isHodSeafarer = isHodRankLabel(selectedRankName)

  if (!isMasterSeafarer && !isHodSeafarer && !form.assignedHodRole) {
    toast.error('Assign To (HOD) is required')
    return
  }

  const activeCrewId = Number(form.crewId || editingComplaint?.crewId || 0)
  const crewSignOnDate = getCrewSignOnDate(activeCrewId, modalSignOnDateByCrewId)
  if (crewSignOnDate) {
    if (form.recordDate && isDateBeforeSignOn(form.recordDate, crewSignOnDate)) {
      toast.error(`Record date cannot be before sign-on date (${crewSignOnDate}).`)
      return
    }
    if (form.reportDate && isDateBeforeSignOn(form.reportDate, crewSignOnDate)) {
      toast.error(`Date of report cannot be before sign-on date (${crewSignOnDate}).`)
      return
    }
  }

  const normalizedComplaintType = normalizeComplaintType(form.complaintType)

  setIsSaving(true)
  try {
    if (editMode === 'create') {
      const payload: CreateCrewComplaintRequest = {
        crewId: form.crewId,
        vesselId: form.vesselId,
        rankId: form.rankId,
        recordDate: form.recordDate || undefined,
        reportDate: form.reportDate || undefined,
        complaintType: normalizedComplaintType || undefined,
        description: form.description,
        assignedHodRole: form.assignedHodRole || undefined,
        hodMasterResponse: form.hodMasterResponse || undefined,
        hodMasterResponseDate: form.hodMasterResponseDate || undefined,
        onboardPreventRecurrence: form.onboardPreventRecurrence || undefined,
        hodSignature: form.hodSignature || undefined,
        masterSignature: form.masterSignature || undefined,
        onboardReviewRole: form.onboardReviewRole || undefined,
        onboardResolved: fromYesNo(form.onboardResolved),
        officeResolved: fromYesNo(form.officeResolved),
      }
      const created = await createCrewComplaint(payload)
      for (const file of pendingFiles) {
        await uploadCrewComplaintAttachment(created.id, file)
      }
      toast.success('Complaint created')
    } else if (editingComplaint) {
      const payload: UpdateCrewComplaintRequest = {
        vesselId: form.vesselId,
        rankId: form.rankId,
        recordDate: form.recordDate || undefined,
        reportDate: form.reportDate || undefined,
        complaintType: normalizedComplaintType || undefined,
        description: form.description || undefined,
        assignedHodRole: form.assignedHodRole || undefined,
        hodMasterResponse: form.hodMasterResponse || undefined,
        hodMasterResponseDate: form.hodMasterResponseDate || undefined,
        onboardPreventRecurrence: form.onboardPreventRecurrence || undefined,
        hodSignature: form.hodSignature || undefined,
        masterSignature: form.masterSignature || undefined,
        onboardReviewRole: form.onboardReviewRole || undefined,
        onboardResolved: fromYesNo(form.onboardResolved),
        officeResolved: fromYesNo(form.officeResolved),
        officeActionTaken: form.officeActionTaken || undefined,
        officeActionDate: form.officeActionDate || undefined,
        officePreventRecurrence: form.officePreventRecurrence || undefined,
        officeWelfareOfficerSignature: form.officeWelfareOfficerSignature || undefined,
        officeResolutionDate: form.officeResolutionDate || undefined,
        authoritiesActionTaken: form.authoritiesActionTaken || undefined,
        authoritiesActionDate: form.authoritiesActionDate || undefined,
        authoritiesPreventRecurrence: form.authoritiesPreventRecurrence || undefined,
        authoritiesWelfareOfficerSignature: form.authoritiesWelfareOfficerSignature || undefined,
        authoritiesResolutionDate: form.authoritiesResolutionDate || undefined,
        officeRemarks: form.officeRemarks || undefined,
      }
      await updateCrewComplaint(editingComplaint.id, payload)
      for (const file of pendingFiles) {
        await uploadCrewComplaintAttachment(editingComplaint.id, file)
      }
      toast.success('Complaint updated')
    }

    setIsEditModalOpen(false)
    setPendingFiles([])
    await fetchComplaints()
  } catch (error: any) {
    showErrorToast(error, 'Failed to save complaint')
  } finally {
    setIsSaving(false)
  }
}

const handleSubmitComplaint = async (item: CrewComplaint) => {
  try {
    await submitCrewComplaint(item.id)
    toast.success('Complaint submitted')
    await fetchComplaints()
  } catch (error: any) {
    showErrorToast(error, 'Failed to submit complaint')
  }
}

const handleApproveReview = async () => {
  if (!reviewComplaint) return

  const reviewStage = getWorkflowStage(reviewComplaint)

  if (reviewStage === 'HOD_REVIEW' || reviewStage === 'MASTER_REVIEW') {
    const hasOnboardResponse = !!(form.hodMasterResponse || '').trim()
    const hasOnboardResponseDate = !!form.hodMasterResponseDate
    const hasOnboardResolved = !!form.onboardResolved
    const hasOnboardPreventRecurrence = !!(form.onboardPreventRecurrence || '').trim()

    if (!hasOnboardResponse) {
      toast.error('Onboard response is required')
      return
    }
    if (!hasOnboardResponseDate) {
      toast.error('Response date is required')
      return
    }
    if (!hasOnboardResolved) {
      toast.error('Resolved onboard is required')
      return
    }
    if (!hasOnboardPreventRecurrence) {
      toast.error('Action to prevent recurrence is required')
      return
    }
  }

  if (reviewStage === 'OFFICE_REVIEW' || reviewStage === 'AUTHORITIES_REVIEW') {
    if (!form.officeResolved) {
      toast.error('Resolved by office is required')
      return
    }
    if (!(form.officeActionTaken || '').trim()) {
      toast.error('Office action taken is required')
      return
    }
    if (!form.officeActionDate) {
      toast.error('Office action date is required')
      return
    }
    if (!(form.officePreventRecurrence || '').trim()) {
      toast.error('Office action to prevent recurrence is required')
      return
    }
    if (!(form.officeWelfareOfficerSignature || '').trim()) {
      toast.error('Office welfare officer signature is required')
      return
    }
    if (!form.officeResolutionDate) {
      toast.error('Office resolution date is required')
      return
    }
  }

  if (reviewStage === 'AUTHORITIES_REVIEW') {
    if (!(form.authoritiesActionTaken || '').trim()) {
      toast.error('Authorities action is required')
      return
    }
    if (!form.authoritiesActionDate) {
      toast.error('Authorities action date is required')
      return
    }
    if (!(form.authoritiesPreventRecurrence || '').trim()) {
      toast.error('Authorities action to prevent recurrence is required')
      return
    }
    if (!(form.authoritiesWelfareOfficerSignature || '').trim()) {
      toast.error('Authorities welfare officer signature is required')
      return
    }
    if (!form.authoritiesResolutionDate) {
      toast.error('Authorities resolution date is required')
      return
    }
  }

  setIsSaving(true)
  try {
    const updated = await updateCrewComplaint(reviewComplaint.id, {
      hodMasterResponse: form.hodMasterResponse || undefined,
      hodMasterResponseDate: form.hodMasterResponseDate || undefined,
      onboardPreventRecurrence: form.onboardPreventRecurrence || undefined,
      hodSignature: form.hodSignature || undefined,
      masterSignature: form.masterSignature || undefined,
      onboardReviewRole: form.onboardReviewRole || undefined,
      onboardResolved: fromYesNo(form.onboardResolved),
      officeResolved: fromYesNo(form.officeResolved),
      officeActionTaken: form.officeActionTaken || undefined,
      officeActionDate: form.officeActionDate || undefined,
      officePreventRecurrence: form.officePreventRecurrence || undefined,
      officeWelfareOfficerSignature: form.officeWelfareOfficerSignature || undefined,
      officeResolutionDate: form.officeResolutionDate || undefined,
      authoritiesActionTaken: form.authoritiesActionTaken || undefined,
      authoritiesActionDate: form.authoritiesActionDate || undefined,
      authoritiesPreventRecurrence: form.authoritiesPreventRecurrence || undefined,
      authoritiesWelfareOfficerSignature: form.authoritiesWelfareOfficerSignature || undefined,
      authoritiesResolutionDate: form.authoritiesResolutionDate || undefined,
      officeRemarks: form.officeRemarks || undefined,
    })
    toast.success(updated.status === 'REVIEWED' ? 'Complaint reviewed' : 'Review saved')
    setIsReviewModalOpen(false)
    await fetchComplaints()
  } catch (error: any) {
    showErrorToast(error, 'Failed to review complaint')
  } finally {
    setIsSaving(false)
  }
}

const deleteComplaintNow = async (item: CrewComplaint) => {
  try {
    await deleteCrewComplaint(item.id)
    toast.success('Complaint deleted')
    await fetchComplaints()
  } catch (error: any) {
    showErrorToast(error, 'Failed to delete complaint')
  }
}

const handleDeleteComplaint = (item: CrewComplaint) => {
  const toastId = `delete-complaint-${item.id}`
  if (toast.isActive(toastId)) return
  toast.info(
    ({ closeToast }) => (
      <div>
        <div className='mb-2'>Delete complaint</div>
        <div className='d-flex gap-2'>
          <button
            className='btn btn-sm btn-danger'
            onClick={async () => {
              closeToast?.()
              await deleteComplaintNow(item)
            }}
          >
            Delete
          </button>
          <button className='btn btn-sm btn-light' onClick={() => closeToast?.()}>
            Cancel
          </button>
        </div>
      </div>
    ),
    {
      toastId,
      autoClose: false,
      closeOnClick: false,
    }
  )
}

const openAttachmentsFor = (item: CrewComplaint | null) => {
  if (!item) return
  setAttachmentsComplaint(item)
  setIsAttachmentsModalOpen(true)
}

const removeAttachment = async (attachmentId: number) => {
  if (!attachmentsComplaint) return
  try {
    await deleteCrewComplaintAttachment(attachmentsComplaint.id, attachmentId)
    const refreshed = await getCrewComplaints()
    setItems(refreshed)
    const updated = refreshed.find((x) => x.id === attachmentsComplaint.id) || null
    if (updated) {
      setAttachmentsComplaint(updated)
      if (editingComplaint?.id === updated.id) {
        setEditingComplaint(updated)
        setForm(mapComplaintToForm(updated))
      }
      if (reviewComplaint?.id === updated.id) {
        setReviewComplaint(updated)
      }
      if (viewComplaint?.id === updated.id) {
        setViewComplaint(updated)
      }
    }
    toast.success('Attachment removed')
  } catch (error: any) {
    showErrorToast(error, 'Failed to remove attachment')
  }
}

const renderBadge = (status: ComplaintStatus) => {
  if (status === 'DRAFT') return <span className='badge badge-light-secondary fw-bold'>Draft</span>
  if (status === 'SUBMITTED') return <span className='badge badge-light-warning fw-bold'>Submitted</span>
  if (status === 'REVIEWED') return <span className='badge badge-light-success fw-bold'>Reviewed</span>
  if (status === 'REJECTED') return <span className='badge badge-light-danger fw-bold'>Rejected</span>
  return <span className='badge badge-light fw-bold'>{status}</span>
}

const renderStageBadge = (item: CrewComplaint) => {
  const stage = getWorkflowStage(item)
  if (!stage || stage === 'CLOSED') return null
  if (stage === 'HOD_REVIEW') return <span className='badge badge-light-primary fw-semibold ms-2'>HOD Review</span>
  if (stage === 'MASTER_REVIEW') return <span className='badge badge-light-primary fw-semibold ms-2'>Review</span>
  if (stage === 'OFFICE_REVIEW') return <span className='badge badge-light-info fw-semibold ms-2'>Office Review</span>
  if (stage === 'AUTHORITIES_REVIEW') return <span className='badge badge-light-danger fw-semibold ms-2'>Authorities Review</span>
  return null
}

const hasReminder = (item: CrewComplaint) => {
  return Boolean(item.authoritiesReminderAt)
}

return (
  <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
    <div className='d-flex flex-column flex-column-fluid'>
      <div id='kt_app_content' className='app-content flex-column-fluid bg-white'>
        <div className='card'>
          <div className='card-header border-0 pt-6 d-flex justify-content-between bg-white'>
            <div>
              <h3 className='card-label fw-bold text-dark'>Crew Complaints</h3>
              <p className='text-muted mb-0 mt-1'>Workflow: Crew to HOD Review to Review to Office Review to Authorities Review.</p>
            </div>
            <div className='card-toolbar'>
              {canCreate && (
                <button
                  className='btn text-white fw-bold shadow-sm d-inline-flex align-items-center gap-2'
                  type='button'
                  style={{
                    backgroundColor: 'rgb(29, 78, 216)',
                    borderColor: 'rgb(29, 78, 216)',
                    borderRadius: '9.75px',
                    padding: '8px 13px',
                    fontSize: '11.5px',
                    fontWeight: 700,
                    boxShadow: 'rgba(0, 0, 0, 0.08) 0px 2px 8px',
                    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
                    gap: '6.5px',
                  }}
                  onClick={openCreate}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='14'
                    height='14'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    style={{ width: '13px', height: '13px' }}
                  >
                    <line x1='12' y1='5' x2='12' y2='19'></line>
                    <line x1='5' y1='12' x2='19' y2='12'></line>
                  </svg>
                  Add Complaint
                </button>
              )}
            </div>
          </div>

          <div className='card-body py-4 bg-white border-top'>
            <div className='row gx-3 gy-3'>
              <div className='col-md-4'>
                <label className='form-label fw-semibold fs-7' style={{ color: '#A1A5B7' }}>{useCrewDropdownFilter ? 'Crew' : 'Search'}</label>
                {useCrewDropdownFilter ? (
                  <select
                    className='form-select'
                    value={selectedCrewId}
                    onChange={(e) => setSelectedCrewId(e.target.value ? Number(e.target.value) : '')}
                  >
                    <option value=''>All Crew</option>
                    {crewFilterOptions.map((crew) => (
                      <option key={crew.id} value={crew.id}>{crew.name}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type='text'
                    className='form-control'
                    placeholder='Search crew'
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                )}
              </div>

              <div className='col-md-2'>
                <label className='form-label fw-semibold fs-7' style={{ color: '#A1A5B7' }}>Month / Year</label>
                <input
                  type='month'
                  className='form-control'
                  value={monthFilter}
                  onChange={(e) => handleMonthFilterChange(e.target.value)}
                />
              </div>

              <div className='col-md-2'>
                <label className='form-label fw-semibold fs-7' style={{ color: '#A1A5B7' }}>Status</label>
                <select className='form-select' value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as 'All' | ComplaintStatus)}>
                  <option value='All'>All Status</option>
                  {(isSuperAdmin || !isOfficeRole) && <option value='DRAFT'>Draft</option>}
                  <option value='SUBMITTED'>Submitted</option>
                  <option value='REVIEWED'>Reviewed</option>
                  <option value='REJECTED'>Rejected</option>
                </select>
              </div>
            </div>
          </div>

          <div className='card-body py-4 bg-white border-top'>
            {reminderItems.length > 0 && (
              <div className='alert alert-warning py-3 px-4 mb-4'>
                {reminderItems.length} complaint(s) have crossed authorities review deadline (20 days). Reminder notification is active.
              </div>
            )}

            <div className='report-table'>
              <div className='table-responsive' onWheel={handleTableHorizontalWheelScroll}>
                <table className='table table-bordered align-middle'>
                    <thead className='table-header text-start'>
                      <tr>
                        <th className='text-center text-nowrap' style={{ minWidth: '70px' }}>Sr/No</th>
                        <th style={{ minWidth: '180px' }}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Name</span>
                            <button type='button' className='btn btn-link p-0 m-0 pb-1' onClick={() => handleSort('seafarerName')}>
                              <KTSVG
                                path={`/media/map/sort-col-${sortConfig.key === 'seafarerName' ? (sortConfig.direction === 'asc' ? 'up-black' : 'down-black') : 'grey'}.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>
                        <th style={{ minWidth: '150px' }}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Date of Report</span>
                            <button type='button' className='btn btn-link p-0 m-0 pb-1' onClick={() => handleSort('reportDate')}>
                              <KTSVG
                                path={`/media/map/sort-col-${sortConfig.key === 'reportDate' ? (sortConfig.direction === 'asc' ? 'up-black' : 'down-black') : 'grey'}.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>
                        <th style={{ minWidth: '180px' }}>
                          <div className='d-flex align-items-center text-nowrap'>
                            <span className='me-1'>Vessel</span>
                            <button type='button' className='btn btn-link p-0 m-0 pb-1' onClick={() => handleSort('vesselName')}>
                              <KTSVG
                                path={`/media/map/sort-col-${sortConfig.key === 'vesselName' ? (sortConfig.direction === 'asc' ? 'up-black' : 'down-black') : 'grey'}.svg`}
                                className='svg-icon-3'
                              />
                            </button>
                          </div>
                        </th>
                        <th style={{ minWidth: '120px' }}>Status</th>
                        <th style={{ minWidth: '150px' }}>Attachments</th>
                        <th style={{ minWidth: '230px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? (
                        <TableSkeleton rows={rowsPerPage || 10} cols={7} />
                      ) : currentRows.length === 0 ? (
                        <tr><td colSpan={7} className='text-center text-muted py-4'>No complaints found.</td></tr>
                      ) : (
                        currentRows.map((item, idx) => {
                          const canEdit = (isSuperAdmin || hasPermission('UPDATE_CREW_COMPLAINT')) && item.status === 'DRAFT'
                          const canDelete = (isSuperAdmin || hasPermission('DELETE_CREW_COMPLAINT')) && item.status === 'DRAFT'
                          const canSubmit = (isSuperAdmin || hasPermission('APPROVE_CREW_COMPLAINTS')) && item.status === 'DRAFT'
                          const stage = getWorkflowStage(item)
                          const effectivelyReviewed =
                            item.status === 'REVIEWED' ||
                            item.onboardResolved === true
                          const canOpenHodReview =
                            !effectivelyReviewed &&
                            (isCrewHod || isSuperAdmin) &&
                            item.status === 'SUBMITTED' &&
                            stage === 'HOD_REVIEW' &&
                            (
                              isSuperAdmin ||
                              !item.assignedHodRole ||
                              (currentUserHodRole != null && item.assignedHodRole === currentUserHodRole)
                            )
                          const canOpenMasterReview =
                            !effectivelyReviewed &&
                            (isCrewMaster || isSuperAdmin) &&
                            item.status === 'SUBMITTED' &&
                            stage === 'MASTER_REVIEW'
                          const canOpenOfficeReview =
                            !effectivelyReviewed &&
                            canReview &&
                            item.status === 'SUBMITTED' &&
                            (stage === 'OFFICE_REVIEW' || stage === 'AUTHORITIES_REVIEW' || !stage)
                          const canOpenReview = canOpenHodReview || canOpenMasterReview || canOpenOfficeReview
                          const reviewLabel = canOpenHodReview
                            ? 'HOD Review'
                            : canOpenMasterReview
                              ? 'Review'
                              : stage === 'AUTHORITIES_REVIEW'
                                ? 'Authorities Review'
                                : 'Office Review'
                          return (
                            <tr key={item.id}>
                              <td className='text-center'>{indexOfFirst + idx + 1}</td>
                              <td>{item.seafarerName || item.crewName || '-'}</td>
                              <td>{toDateInput(item.reportDate) || '-'}</td>
                              <td>{item.vesselName || '-'}</td>
                              <td>
                                {renderBadge(item.status)}
                                {renderStageBadge(item)}
                                {hasReminder(item) && <span className='badge badge-danger ms-2'>Reminder</span>}
                              </td>
                              <td>
                                <button
                                  type='button'
                                  className='btn btn-sm btn-light-primary text-nowrap'
                                  style={{ whiteSpace: 'nowrap' }}
                                  onClick={() => openAttachmentsFor(item)}
                                >
                                  View Files ({item.attachmentCount || 0})
                                </button>
                              </td>
                              <td>
                                <div className='d-flex gap-2'>
                                  <button className='btn btn-icon btn-sm' title='View' onClick={() => openView(item)}>
                                    <KTSVG path='/media/map/ph_eye.svg' className='svg-icon-3 text-primary' />
                                  </button>
                                  {canEdit && (
                                    <button className='btn btn-icon btn-sm' title='Edit' onClick={() => openEdit(item)}>
                                      <KTSVG path='/media/map/edit-active.svg' className='svg-icon-3' />
                                    </button>
                                  )}
                                  {canDelete && (
                                    <button className='btn btn-icon btn-sm' title='Delete' onClick={() => handleDeleteComplaint(item)}>
                                      <KTSVG path='/media/map/red-cross-icon.svg' className='svg-icon-3' />
                                    </button>
                                  )}
                                  {canSubmit && (
                                    <button className='btn btn-light-primary btn-sm' onClick={() => handleSubmitComplaint(item)}>Submit</button>
                                  )}
                                  {canOpenReview && (
                                    <button
                                      className='btn btn-light-warning btn-sm'
                                      onClick={() => openReview(item)}
                                    >
                                      {reviewLabel}
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )
                        })
                      )}
                    </tbody>
                  </table>
                </div>

              <div className='d-flex justify-content-between align-items-center py-4 px-2 border-top'>
                <div className='d-flex align-items-center gap-3'>
                  <span className='text-muted mb-0'>Rows per page</span>
                  <select
                    className='form-select form-select-sm rounded-pill'
                    style={{ width: 92 }}
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value))
                      setCurrentPage(1)
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>

                <div className='d-flex align-items-center gap-3'>
                  <span className='text-muted small'>
                    Showing <strong>{sortedItems.length ? indexOfFirst + 1 : 0}-{Math.min(indexOfLast, sortedItems.length)}</strong> of <strong>{sortedItems.length}</strong>
                  </span>
                  <div className='d-flex align-items-center gap-2'>
                    <button
                      type='button'
                      className='btn btn-light btn-sm'
                      style={{ minWidth: 40, height: 40 }}
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    >
                      {'<'}
                    </button>
                    <button
                      type='button'
                      className='btn btn-light btn-sm'
                      style={{ minWidth: 40, height: 40 }}
                      disabled
                    >
                      {Math.max(1, currentPage)}
                    </button>
                    <button
                      type='button'
                      className='btn btn-light btn-sm'
                      style={{ minWidth: 40, height: 40 }}
                      disabled={currentPage >= Math.max(1, totalPages)}
                      onClick={() => setCurrentPage((p) => Math.min(Math.max(1, totalPages), p + 1))}
                    >
                      {'>'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <CrewComplaintModal
      isOpen={isEditModalOpen}
      isSaving={isSaving}
      isSuperAdmin={isSuperAdmin}
      isCrew={isCrew}
      isCrewMaster={isCrewMaster}
      mode={editMode}
      form={form}
      ranks={ranks}
      crewMembers={modalCrewMembers}
      companyGroups={companyGroups}
      subCompanies={subCompanies}
      vessels={effectiveVessels}
      pendingFiles={pendingFiles}
      onFieldChange={setField}
      onPendingFilesChange={setPendingFiles}
      onSave={saveComplaint}
      onClose={() => setIsEditModalOpen(false)}
      crewSignOnDateById={modalSignOnDateByCrewId}
      onOpenAttachments={() => {
        if (editingComplaint) openAttachmentsFor(editingComplaint)
      }}
    />

    <ViewCrewComplaintModal
      visible={isViewModalOpen}
      complaint={viewComplaint}
      onClose={() => setIsViewModalOpen(false)}
    />

    <CrewComplaintReviewModal
      visible={isReviewModalOpen}
      complaint={reviewComplaint}
      mode={
        getWorkflowStage(reviewComplaint) === 'HOD_REVIEW'
          ? 'hod'
          : getWorkflowStage(reviewComplaint) === 'MASTER_REVIEW'
            ? 'master'
            : getWorkflowStage(reviewComplaint) === 'AUTHORITIES_REVIEW'
              ? 'authorities'
              : 'office'
      }
      loginUsername={loginUsername}
      form={form}
      isSaving={isSaving}
      onFieldChange={setField}
      onClose={() => setIsReviewModalOpen(false)}
      onApprove={handleApproveReview}
      onOpenAttachments={() => {
        if (reviewComplaint) openAttachmentsFor(reviewComplaint)
      }}
    />

    <CrewComplaintAttachmentsModal
      visible={isAttachmentsModalOpen}
      title={`Attachments - ${attachmentsComplaint?.seafarerName || attachmentsComplaint?.crewName || 'Complaint'}`}
      items={(attachmentsComplaint?.attachments || []).map((att) => ({
        id: att.id,
        fileName: att.fileName,
        fileType: att.fileType,
        fileSize: att.fileSize,
        viewUrl: crewComplaintAttachmentViewUrl(attachmentsComplaint!.id, att.id),
        downloadUrl: crewComplaintAttachmentDownloadUrl(attachmentsComplaint!.id, att.id),
      }))}
      allowRemove={true}
      onRemove={removeAttachment}
      onClose={() => setIsAttachmentsModalOpen(false)}
      onViewAttachment={(item) =>
        setFileModal({
          visible: true,
          title: item.fileName,
          fileName: item.fileName,
          viewUrl: item.viewUrl,
          downloadUrl: item.downloadUrl,
        })
      }
    />

    <FileViewerModal
      visible={fileModal.visible}
      title={fileModal.title}
      fileName={fileModal.fileName}
      viewUrl={fileModal.viewUrl}
      downloadUrl={fileModal.downloadUrl}
      onClose={() => setFileModal((prev) => ({ ...prev, visible: false }))}
    />

    <ToastContainer
      position='top-center'
      autoClose={3000}
      hideProgressBar={false}
      newestOnTop={false}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      style={{ top: '5rem' }}
    />
  </div>
)
}

export default CrewComplaints
